import api from "./api";
import { CREATE_URL, updateUrl, deleteUrl, statusToggleUrl, trendingToggleUrl, ACTIVE_COUNT_URL } from "./api";

/**
 * Fetch the article list for a category.
 * The API returns { page, per_page, total, category, has_next_page, data: [...] }
 */
export async function fetchArticles(endpoint) {
  const res = await api.get(endpoint);
  const payload = res.data || {};
  return {
    items: Array.isArray(payload.data) ? payload.data : [],
    total: payload.total ?? payload.data?.length ?? 0,
    page: payload.page ?? 1,
    perPage: payload.per_page ?? null,
  };
}

/**
 * Fetch a single article.
 * There's no dedicated single-post API, so this fetches the category's
 * list and finds the matching id client-side. (Components should prefer
 * passing the post via router state when navigating from a list they
 * already fetched — this is the fallback for direct links/refreshes.)
 */
export async function fetchArticleById(endpoint, id) {
  const { items } = await fetchArticles(endpoint);
  const found = items.find((item) => String(item.id) === String(id));
  if (!found) throw new Error("Post not found.");
  return found;
}

/**
 * Build a FormData payload matching the confirmed Laravel validator:
 *   category (required, must be one of the 9 exact category labels)
 *   title (required)
 *   summary (nullable)
 *   description (required)
 *   tags (nullable, array or string)
 *   image (nullable, file, max 10MB)
 *   status (required boolean)
 *   trending (required boolean)
 */
function buildFormData(article) {
  const fd = new FormData();
  fd.append("category", article.category ?? "");
  fd.append("title", article.title ?? "");
  fd.append("summary", article.summary ?? "");
  fd.append("description", article.description ?? "");

  const tags = Array.isArray(article.tags) ? article.tags : [];
  tags.forEach((tag) => fd.append("tags[]", tag));

  // status/trending are required booleans — send as "1"/"0".
  fd.append("status", article.status ? "1" : "0");
  fd.append("trending", article.trending ? "1" : "0");

  // `image` is nullable — only send it if the user picked/changed a file.
  if (article.imageFile instanceof File) {
    fd.append("image", article.imageFile);
  }

  return fd;
}

/**
 * Create a new article.
 * POST https://todaytalks.mpdatahub.com/api/talksstore
 * (single shared endpoint for every category — `category` is a field, not a path segment)
 */
export async function createArticle(article) {
  const fd = buildFormData(article);
  const res = await api.post(CREATE_URL, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

/**
 * Update an existing article.
 * POST https://todaytalks.mpdatahub.com/api/talksupdate/{id}
 * Laravel validates the full payload again (category/title/description/status/trending
 * are all required), so this always sends the complete current article, not a partial diff.
 * (Status/trending have their own lightweight toggle endpoints below — this
 * full update is only used by the Create/Edit form.)
 */
export async function updateArticle(id, article) {
  const fd = buildFormData(article);
  const res = await api.post(updateUrl(id), fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

/**
 * Toggle just the status flag.
 * GET https://todaytalks.mpdatahub.com/api/talks/{id}/status?category=...&status=1|0
 * Lightweight — only sends id/category/new value, not the full article.
 */
export async function toggleStatus(id, category, nextStatus) {
  const res = await api.get(statusToggleUrl(id), {
    params: { category, status: nextStatus ? 1 : 0 },
  });
  return res.data;
}

/**
 * Toggle just the trending flag.
 * GET https://todaytalks.mpdatahub.com/api/talks/{id}/trending?category=...&trending=1|0
 */
export async function toggleTrending(id, category, nextTrending) {
  const res = await api.get(trendingToggleUrl(id), {
    params: { category, trending: nextTrending ? 1 : 0 },
  });
  return res.data;
}

/**
 * Delete an article.
 * ⚠️ Not confirmed by you — guessed as DELETE https://todaytalks.mpdatahub.com/api/talksdelete/{id}
 * to match the talksstore/talksupdate naming convention. Update `deleteUrl` in api.js
 * if your real delete route is different.
 */
export async function deleteArticle(id, cat) {
  const res = await api.get(deleteUrl(id, cat));
  return res.data;
}

/**
 * Active post counts per category, for the overview bar chart.
 * GET https://todaytalks.mpdatahub.com/api/talks/active-count
 * Returns { total_active, category_counts: { <CategoryLabel>: count, ... } }
 */
export async function fetchActiveCounts() {
  const res = await api.get(ACTIVE_COUNT_URL);
  const payload = res.data?.data || {};
  return {
    totalActive: payload.total_active ?? 0,
    categoryCounts: payload.category_counts || {},
  };
}

/**
 * The category-specific LIST endpoints store tags as a messy, sometimes
 * double/triple-JSON-encoded string. This safely unwraps it down to a
 * flat array of plain strings for display / editing.
 */
export function parseTags(raw) {
  let value = raw;
  for (let i = 0; i < 5; i++) {
    if (typeof value !== "string") break;
    try {
      value = JSON.parse(value);
    } catch {
      break;
    }
  }
  if (Array.isArray(value)) {
    return value
      .flat(Infinity)
      .map((t) => String(t).trim())
      .filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
}
